import setImmediate from 'core-js/stable/set-immediate'

export { VueEventTracker }

/*
  VueEventTracker plugin

  Adds a $sendEvent(evtName, evtData, evtOptions) method on the Vue instance
  to enable components to send analytics events with data fields cumulatively
  collected from the component hierarchy, exposed by each component's eventContext.

  Child component eventContext fields overwrites ancestor components' if there
  is overlap.

  The cumulative eventContext is sent under the "data" field of the event payload.

  A special field defined in eventContext (__vue_event_tracker_event_metadata)
  is extracted and merged with event options, rather than being embedded in
  the event's "data" field.

  NOTE: The $sendEvent function is designed to only fire an event after a user object has been retrieved
  or it has been confirmed that the user is not signed in. It does this by referencing the user store
  in VueX, which has a boolean fetching that is true by default, and only switches to false after the
  user's info has been checked. This creates a tight dependency.
*/
class VueEventTracker {
  constructor(...instantiationHooks) {
    this.eventHooks = []
    for (let hook of instantiationHooks) {
      this.registerEventHook(hook)
    }
  }

  registerEventHook(hook) {
    if (typeof hook != 'function') {
      console.warn(`${pluginIdentifier} Hook should be function but is ${typeof f}`)
      return
    }
    this.eventHooks.push(hook)
  }

  callHooks(name, data, options) {
    for (let hookFunction of this.eventHooks) {
      try {
        hookFunction(name, data, options)
      } catch (error) {
        console.warn(`${pluginIdentifier} Error in event hook ${hookFunction.name}: ${error}`)
      }
    }
  }

  install(Vue, options) {
    let sendEvent = (vm, name, data = {}, options) => {
      let evtData = { ...data }
      if (vm.$route) {
        evtData.page_name = vm.$route.name
      }

      while (vm.$parent) {
        let f = vm.$options.eventContext ? vm.$options.eventContext.bind(vm) : false
        let componentIdentifier = vm.$options._componentTag
          ? vm.$options._componentTag
          : vm.$vnode
            ? vm.$vnode.tag
            : 'UNKNOWN-COMPONENT-TAG'
        vm = vm.$parent

        if (!f) {
          continue
        }

        if (typeof f != 'function') {
          console.warn(
            `${pluginIdentifier} eventContext of ${componentIdentifier} should be function but is ${typeof f}`
          )
          continue
        }

        let ctx = f()
        if (typeof ctx != 'object') {
          console.warn(
            `${pluginIdentifier} eventContext of ${componentIdentifier} should return an object but returns ${typeof ctx}`
          )
          continue
        }

        // Child fieds overwrite parent fields
        evtData = { ...ctx, ...evtData }
      }

      // extract metadata and remove from evtData
      const eventMetadata = evtData.__vue_event_tracker_event_metadata
      delete evtData.__vue_event_tracker_event_metadata
      this.callHooks(name, evtData, {
        ...options,
        ...eventMetadata
      })
    }

    Vue.prototype.$sendEvent = function (name, data, options) {
      if (this.$store && this.$store._modules.root._children.user.state.fetching) {
        this.$store.commit('user/pushOnetimeCallback', () => {
          setImmediate(sendEvent.bind(null, this, name, data, options))
        })
      } else {
        setImmediate(sendEvent.bind(null, this, name, data, options))
      }
    }
  }
}

let pluginIdentifier = VueEventTracker.name
